/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import {
  CloseOutlined,
  DeleteOutlined,
  WarningOutlined,
} from '@ant-design/icons'
import { App, Modal, Alert } from 'antd'
import colors from '@sportninja/common/constants/appColors'
import { useCallback, useState, useEffect, useMemo } from 'react'
import { useModalScroll } from 'src/hooks/useModalScroll'
import Text from 'src/components/ts-components/Text'
import useWindowSize from 'src/hooks/useWindowSize'
import Button from 'src/components/ts-components/Button'
import req from '@sportninja/common/api/request'
import { getErrorMessage } from '@sportninja/common/utils/utils'
import { TextInput } from 'src/components/ts-components/TextInput'
import { paragraphMedium, paragraphSmall, weightRegular } from '../css'
import { isCanlan } from '@sportninja/common/utils/customer-name'

const handleDeleteAccount = async (
  userId: string | undefined,
  onLogout: (() => Promise<void>) | undefined
) => {
  if (!userId) {
    throw new Error('User ID is required')
  }
  if (!onLogout) {
    throw new Error('Logout function is required')
  }

  await req(`/users/${userId}`, {
    method: 'DELETE',
  })
  // On success, clear user and logout
  if (onLogout) {
    await onLogout()
  }
}

const DeleteButton = ({ ...props }) => {
  return (
    <button
      css={css`
        display: flex;
        padding: 8px 16px 8px 8px;
        justify-content: center;
        align-items: center;
        gap: 8px;
        border-radius: 8px;
        border: 1px solid ${colors.ERROR_200};
        background: ${colors.ERROR_200};
        box-shadow: 0px 2px 0px 0px rgba(5, 145, 255, 0.1);
      `}
      {...props}
    >
      <DeleteOutlined
        css={css`
          font-size: 24px;
          color: ${colors.NEUTRAL_0};
        `}
        width={24}
        height={24}
      />
      <Text variant='heading6' weight='semiBold' color={colors.NEUTRAL_0}>
        DELETE USER
      </Text>
    </button>
  )
}

const styles = css`
  .my-modal-body {
  }
  .my-modal-mask {
    backdrop-filter: blur(1px);
  }
  .my-modal-header {
    border-bottom: 1px dotted ${colors.FIERY_RED};
  }
  .my-modal-footer {
    color: ${colors.FIERY_RED};
  }
  .my-modal-content {
    border: 1px solid #333;
  }
`
const classNames = {
  body: styles['my-modal-body'],
  mask: styles['my-modal-mask'],
  header: styles['my-modal-header'],
  footer: styles['my-modal-footer'],
  content: styles['my-modal-content'],
}
const modalStyles = {
  header: {
    backgroundColor: colors.SECONDARY_300,
    paddingLeft: '16px',
    paddingRight: '16px',
  },
  body: {
    borderBottom: `1px solid ${colors.SOFT_STEEL}`,
    borderTop: `1px solid ${colors.SOFT_STEEL}`,
    backgroundColor: colors.SECONDARY_300,
  },
  content: {
    backgroundColor: colors.SECONDARY_300,
    paddingTop: '8px',
    paddingBottom: '16px',
    paddingLeft: '0',
    paddingRight: '0',
  },
  wrapper: {
    zIndex: 10001,
  },
  mask: {
    backdropFilter: 'blur(2px)',
    backgroundColor: 'rgba(6, 10, 16, 0.8)',
    zIndex: 10000,
  },
  footer: {
    paddingLeft: '16px',
    paddingRight: '16px',
  },
}

/**
 * Delete confirmation modal component
 */
const DeleteConfirmationModal = ({
  isOpen,
  onClose,
  userEmail,
  isLoading,
  onConfirm,
  error,
}: {
  isOpen: boolean
  onClose: () => void
  onConfirm: () => void
  userEmail: string
  isLoading: boolean
  error: string | null
}) => {
  const { width } = useWindowSize()
  const isMobile = useMemo(() => width <= 768, [width])
  const [confirmText, setConfirmText] = useState('')

  const isDeleteEnabled = confirmText?.toLowerCase() === 'delete'

  /**
   * Handles modal close and resets state
   */
  const handleClose = useCallback(() => {
    setConfirmText('')
    onClose()
  }, [onClose])

  /**
   * Renders the close icon
   */
  const closeIcon = useCallback(() => {
    return (
      <CloseOutlined
        css={css`
          color: ${colors.SOLITUDE};
          font-size: 18px;
          cursor: pointer;
          transition: all 0.2s;
          &:hover {
            color: ${colors.WHITE};
          }
        `}
      />
    )
  }, [])

  return (
    <Modal
      css={css`
        display: flex;
        flex: 1;
        height: ${isMobile ? '100vh' : 'auto'} !important;
        margin: ${isMobile ? '0' : '24px'};
        padding: 0;
        max-width: ${isMobile ? '100%' : '480px'};
        top: ${isMobile ? '0' : '50%'};
        .ant-modal-content {
          height: ${isMobile ? '100vh' : 'auto'} !important;
          border-radius: ${isMobile ? '0' : '16px'};
          display: flex;
          flex-direction: column;
          background: ${colors.SECONDARY_300};
          border: 1px solid ${colors.SECONDARY_100};
        }
        .ant-modal-body {
          flex: 1;
          overflow: hidden;
          display: flex;
          flex-direction: column;
          padding: 24px;
        }
        .ant-modal-header {
          display: none;
        }
      `}
      width={isMobile ? '100%' : 480}
      closeIcon={closeIcon()}
      open={isOpen}
      onCancel={handleClose}
      centered={!isMobile}
      maskClosable={false}
      footer={null}
      styles={{
        content: {
          backgroundColor: colors.SECONDARY_300,
          padding: 0,
        },
        wrapper: {
          zIndex: 10002,
        },
        mask: {
          backdropFilter: 'blur(2px)',
          backgroundColor: 'rgba(6, 10, 16, 0.8)',
          zIndex: 10001,
        },
      }}
    >
      <div
        css={css`
          display: flex;
          flex-direction: column;
          justify-content: flex-start;
          align-items: center;
          gap: 16px;
          position: relative;
        `}
      >
        {/* Warning Icon */}
        <WarningOutlined
          css={css`
            font-size: 48px;
            color: ${colors.ERROR_200};
          `}
        />

        {/* Title */}
        <Text
          variant='heading1'
          weight='semiBold'
          color={colors.WHITE}
          style={`
            font-family: ${
              isCanlan
                ? 'Neue Plak Condensed, Arial, Helvetica, sans-serif;'
                : 'BarlowCondensed, Arial, Helvetica, sans-serif;'
            };
            font-size: 36px;
            font-weight: 600;
            line-height: 36px;
            text-align: center;
          `}
        >
          Delete User
        </Text>

        {/* Description */}
        <div
          css={css`
            align-self: stretch;
            flex-direction: column;
            justify-content: flex-start;
            align-items: flex-start;
            gap: 8px;
            display: flex;
          `}
        >
          <div
            css={css`
              align-self: stretch;
              text-align: center;
            `}
          >
            <Text
              variant='paragraphMedium'
              weight='regular'
              color={colors.WHITE}
              style={`
                font-family: Inter, Arial, Helvetica, sans-serif;
                font-size: 16px;
                font-weight: 400;
                line-height: 23.2px;
              `}
            >
              This action will permanently remove the user{' '}
              <span
                css={css`
                  font-weight: 700;
                `}
              >
                {userEmail}
              </span>{' '}
              and all associated data from{' '}
              {isCanlan ? 'Canlan Stats' : 'SportNinja'}. This action cannot be
              undone.
              <br />
              <br />
              To confirm, please type{' '}
              <span
                css={css`
                  font-weight: 700;
                `}
              >
                DELETE
              </span>{' '}
              in the field below.
            </Text>
          </div>
        </div>

        {/* Confirmation Input */}
        <div
          css={css`
            align-self: stretch;
            height: 69px;
            flex-direction: column;
            justify-content: flex-start;
            align-items: flex-start;
            gap: 8px;
            display: flex;
          `}
        >
          <div
            css={css`
              justify-content: flex-start;
              align-items: center;
              gap: 4px;
              display: inline-flex;
            `}
          >
            <Text
              variant='captionLarge'
              weight='regular'
              color={colors.WHITE}
              style={`
                font-family: Inter, Arial, Helvetica, sans-serif;
                font-size: 14px;
                font-weight: 400;
                line-height: 20.3px;
              `}
            >
              Type DELETE to confirm:
            </Text>
          </div>
          <div
            css={css`
              align-self: stretch;
              padding: 8px 16px;
              background: ${colors.SECONDARY_200};
              border-radius: 8px;
              border: 1px solid ${colors.SECONDARY_100};
              justify-content: space-between;
              align-items: center;
              display: inline-flex;
            `}
          >
            <input
              css={css`
                ${paragraphSmall}
                ${weightRegular}
                text-transform: uppercase;
                background: transparent;
                border: none;
                outline: none;
                color: ${colors.WHITE};
                width: 100%;
                &::placeholder {
                  color: ${colors.NEUTRAL_50};
                }
              `}
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder=''
              autoComplete='off'
            />
          </div>
        </div>

        {error && (
          <Alert
            message={error}
            type='error'
            css={css`
              width: 100%;
            `}
          />
        )}

        {/* Divider */}
        <div
          css={css`
            align-self: stretch;
            height: 1px;
            background: ${colors.SECONDARY_100};
          `}
        />

        {/* Action Buttons */}
        <div
          css={css`
            align-self: stretch;
            justify-content: center;
            align-items: flex-start;
            gap: 16px;
            display: inline-flex;
          `}
        >
          <button
            css={css`
              flex: 1 1 0;
              padding: 8px 24px;
              background: ${colors.ERROR_200};
              border-radius: 8px;
              border: 2px solid ${colors.ERROR_200};
              justify-content: center;
              align-items: center;
              gap: 8px;
              display: flex;
              cursor: ${isDeleteEnabled && !isLoading
                ? 'pointer'
                : 'not-allowed'};
              opacity: ${isDeleteEnabled && !isLoading ? 1 : 0.5};
              transition: all 0.2s;
              &:hover {
                opacity: ${isDeleteEnabled && !isLoading ? 0.8 : 0.5};
              }
            `}
            onClick={isDeleteEnabled && !isLoading ? onConfirm : undefined}
            disabled={!isDeleteEnabled || isLoading}
          >
            <Text
              variant='heading6'
              weight='semiBold'
              color={colors.WHITE}
              style={`
                font-family: ${
                  isCanlan
                    ? 'Neue Plak Condensed, Arial, Helvetica, sans-serif;'
                    : 'BarlowCondensed, Arial, Helvetica, sans-serif;'
                };
                font-size: 20px;
                font-weight: 600;
                line-height: 24px;
              `}
            >
              {isLoading ? 'DELETING...' : 'DELETE USER'}
            </Text>
          </button>
          <button
            css={css`
              flex: 1 1 0;
              padding: 8px 24px;
              border-radius: 8px;
              border: 2px solid ${colors.WHITE};
              background: transparent;
              justify-content: center;
              align-items: center;
              gap: 8px;
              display: flex;
              cursor: pointer;
              transition: all 0.2s;
              &:hover {
                background: rgba(255, 255, 255, 0.1);
              }
            `}
            onClick={handleClose}
            disabled={isLoading}
          >
            <Text
              variant='heading6'
              weight='semiBold'
              color={colors.WHITE}
              style={`
                font-family: ${
                  isCanlan
                    ? 'Neue Plak Condensed, Arial, Helvetica, sans-serif;'
                    : 'BarlowCondensed, Arial, Helvetica, sans-serif;'
                };
                font-size: 20px;
                font-weight: 600;
                line-height: 24px;
              `}
            >
              CANCEL
            </Text>
          </button>
        </div>
      </div>
    </Modal>
  )
}

type UserSettingsModalProps = {
  isOpen: boolean
  onClose: () => void
  onPasswordChange?: (values: any) => Promise<any>
  onLogout?: () => Promise<void>
  userEmail?: string
  userId?: string
}

const UserSettingsModal = ({
  isOpen,
  onClose,
  onPasswordChange,
  onLogout,
  userEmail = 'user@sportninja.com',
  userId,
}: UserSettingsModalProps) => {
  const { width } = useWindowSize()
  const isMobile = useMemo(() => width <= 768, [width])
  const { message } = App.useApp()
  const { openModal, closeModal } = useModalScroll()

  // Password form state
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')

  // Loading and error states
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Delete confirmation modal state
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)

  // Form validation
  const [formData, setFormData] = useState({
    fieldErrors: [] as string[],
    customError: {} as Record<string, string>,
  })

  /**
   * Handles opening the delete confirmation modal
   */
  const handleDeleteClick = useCallback(() => {
    setError(null)
    setIsDeleteModalOpen(true)
  }, [])

  /**
   * Handles closing the delete confirmation modal
   */
  const handleDeleteModalClose = useCallback(() => {
    setIsDeleteModalOpen(false)
  }, [])

  /**
   * Handles user deletion confirmation
   */
  const handleDeleteConfirm = useCallback(async () => {
    try {
      setIsDeleting(true)
      await handleDeleteAccount(userId, onLogout)
      message.success('User deleted successfully')
      setIsDeleteModalOpen(false)
      onClose()
    } catch (e: any) {
      const errorMessage = getErrorMessage(e)
      setError(errorMessage || 'Failed to delete user')
    } finally {
      setIsDeleting(false)
    }
  }, [userId, onLogout, message, onClose])

  /**
   * Handles password change form submission
   */
  const handlePasswordChange = useCallback(async () => {
    try {
      setIsLoading(true)
      setError(null)

      // Reset form errors
      setFormData({
        fieldErrors: [],
        customError: {},
      })

      // Validate passwords
      if (!currentPassword) {
        setCustomErrorByField('currentPassword', 'Current password is required')
        return
      }

      if (!newPassword) {
        setCustomErrorByField('newPassword', 'New password is required')
        return
      }

      if (newPassword.length < 8) {
        setCustomErrorByField(
          'newPassword',
          'Password must be at least 8 characters long'
        )
        return
      }

      if (newPassword !== confirmPassword) {
        setCustomErrorByField('confirmPassword', 'Passwords do not match')
        return
      }

      const values = {
        password_old: currentPassword,
        password: newPassword,
        confirm_password: confirmPassword,
      }

      if (onPasswordChange) {
        await onPasswordChange(values)
      } else {
        // Default password change implementation
        await req('/auth/change-password', {
          method: 'PUT',
          body: values,
        })
      }

      message.success('Password Changed Successfully')

      // Reset form
      setCurrentPassword('')
      setNewPassword('')
      setConfirmPassword('')

      onClose()
    } catch (e: any) {
      if (e?.invalid_fields) {
        setCustomErrors(e?.invalid_fields)
        return
      }
      const errorMessage = getErrorMessage(e)
      setError(errorMessage || 'Failed to change password')
    } finally {
      setIsLoading(false)
    }
  }, [
    currentPassword,
    newPassword,
    confirmPassword,
    onPasswordChange,
    message,
    onClose,
  ])

  /**
   * Sets custom error for a specific field
   */
  const setCustomErrorByField = (field: string, message: string) => {
    setFormData((prev) => ({
      ...prev,
      customError: {
        ...prev.customError,
        [field]: message,
      },
      fieldErrors: [...new Set([...prev.fieldErrors, field])],
    }))
  }

  /**
   * Sets custom errors from API response
   */
  const setCustomErrors = (invalid_fields: Record<string, string>) => {
    Object.entries(invalid_fields).forEach(([field, message]) => {
      setCustomErrorByField(field, message)
    })
  }

  /**
   * Handles modal close
   */
  const handleClose = useCallback(() => {
    // Reset form state
    setCurrentPassword('')
    setNewPassword('')
    setConfirmPassword('')
    setError(null)
    setFormData({
      fieldErrors: [],
      customError: {},
    })
    onClose()
  }, [onClose])

  useEffect(() => {
    if (isOpen) {
      openModal()
    } else {
      closeModal()
    }
  }, [closeModal, isOpen, openModal])

  /**
   * Renders the modal header
   */
  const getHeader = useCallback(() => {
    return (
      <Text variant='heading3' weight='semiBold' color={colors.WHITE}>
        User Settings
      </Text>
    )
  }, [])

  /**
   * Renders the modal footer
   */
  const getFooter = useCallback(() => {
    return (
      <div
        css={css`
          display: flex;
          justify-content: flex-end;
          gap: 16px;
          width: ${isMobile ? '100%' : '50%'};
          margin-left: ${isMobile ? '0' : 'auto'};
          @media (max-width: 768px) {
            width: 100%;
            flex-direction: column;
          }
        `}
      >
        <Button text='Cancel' onClick={handleClose} variant='secondary' />
        <Button
          text={isLoading ? 'Submitting...' : 'Submit'}
          onClick={handlePasswordChange}
          variant='primary'
          disabled={
            isLoading || !currentPassword || !newPassword || !confirmPassword
          }
        />
      </div>
    )
  }, [
    isMobile,
    handleClose,
    handlePasswordChange,
    isLoading,
    currentPassword,
    newPassword,
    confirmPassword,
  ])

  /**
   * Renders the close icon
   */
  const closeIcon = useCallback(() => {
    return (
      <CloseOutlined
        css={css`
          color: ${colors.SOLITUDE};
          font-size: 18px;
          cursor: pointer;
          transition: all 0.2s;
          &:hover {
            color: ${colors.WHITE};
          }
        `}
      />
    )
  }, [])

  return (
    <Modal
      css={css`
        display: flex;
        flex: 1;
        height: ${isMobile ? '100vh' : 'auto'} !important;
        margin: ${isMobile ? '0' : '24px'};
        padding: 0;
        max-width: ${isMobile ? '100%' : '40vw'};
        top: ${isMobile ? '0' : '50%'};
        .ant-modal-content {
          height: ${isMobile ? '100vh' : 'auto'} !important;
          border-radius: ${isMobile ? '0' : '8px'};
          display: flex;
          flex-direction: column;
        }
        .ant-modal-body {
          flex: 1;
          overflow: hidden;
          display: flex;
          flex-direction: column;
          padding: 16px 16px;
        }
        .ant-tabs-tabpane {
          background: ${colors.SECONDARY_300} !important;
        }
        .ant-tabs-nav {
          min-height: ${isMobile ? 'auto' : '600px'} !important;
        }
        .ant-tabs-tab {
          height: 40px !important;
        }
        /* if the tab is active, change the background color */
        .ant-tabs-tab-active {
          background-color: #26303e;
        }
        .ant-tabs-tab-btn {
          ${paragraphMedium}
          ${weightRegular}
        }
        .ant-tabs-tab-btn[aria-selected='true'] {
          color: ${colors.PRIMARY}!important;
        }
        .ant-tabs-tab-btn[aria-selected='false'] {
          color: ${colors.NEUTRAL_30}!important;
        }
        .ant-tabs-tab.ant-tabs-tab-disabled {
          color: ${colors.NEUTRAL_30}!important;
          cursor: not-allowed !important;
          opacity: 0.5 !important;
        }
      `}
      width={isMobile ? '100%' : 538}
      closeIcon={closeIcon()}
      title={getHeader()}
      footer={getFooter()}
      open={isOpen}
      onCancel={handleClose}
      centered={!isMobile}
      maskClosable={false}
      classNames={classNames}
      styles={modalStyles}
    >
      <div
        css={css`
          display: flex;
          flex-direction: column;
          gap: 16px;
          width: 100%;
          padding: 0 8px;
        `}
      >
        {/* Password Change Section */}
        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: 16px;
          `}
        >
          {/* Section Header */}
          <Text
            variant='captionLarge'
            weight='semiBold'
            color={colors.WHITE}
            style={`
              font-family: Inter, Arial, Helvetica, sans-serif;
              font-size: 14px;
              font-weight: 600;
              text-transform: uppercase;
              letter-spacing: 1.68px;
            `}
          >
            Change Password
          </Text>

          {/* Current Password */}
          <div
            css={css`
              display: flex;
              flex-direction: column;
              gap: 8px;
            `}
          >
            <TextInput
              name='currentPassword'
              value={currentPassword}
              setValue={setCurrentPassword}
              label='Current Password'
              type='password'
              placeholder='•••••••••'
              autoComplete='current-password'
              fieldErrors={formData.fieldErrors}
              customError={formData.customError}
            />
          </div>

          {/* New Password Fields */}
          <div
            css={css`
              display: flex;
              gap: 16px;
              @media (max-width: 768px) {
                flex-direction: column;
              }
            `}
          >
            {/* New Password */}
            <div
              css={css`
                flex: 1;
                display: flex;
                flex-direction: column;
                gap: 8px;
              `}
            >
              <TextInput
                name='newPassword'
                value={newPassword}
                setValue={setNewPassword}
                label='New Password'
                type='password'
                placeholder='•••••••••'
                autoComplete='new-password'
                fieldErrors={formData.fieldErrors}
                customError={formData.customError}
              />
            </div>

            {/* Confirm New Password */}
            <div
              css={css`
                flex: 1;
                display: flex;
                flex-direction: column;
                gap: 8px;
              `}
            >
              <TextInput
                name='confirmPassword'
                value={confirmPassword}
                setValue={setConfirmPassword}
                label='Confirm New Password'
                type='password'
                placeholder='•••••••••'
                autoComplete='new-password'
                fieldErrors={formData.fieldErrors}
                customError={formData.customError}
              />
            </div>
          </div>
        </div>

        {/* Danger Zone Section */}
        <>
          <div
            css={css`
              height: 1px;
              background-color: ${colors.SECONDARY_100};
              width: 100%;
            `}
          />
          <div
            css={css`
              display: flex;
              flex-direction: column;
              align-items: flex-start;
              gap: 8px;
              flex: 1 0 0;
              width: 100%;
            `}
          >
            <Text
              variant='captionSmall'
              weight='semiBold'
              color={colors.NEUTRAL_50}
            >
              Danger Zone
            </Text>
            <Text
              variant='paragraphXSmall'
              weight='regular'
              color={colors.WHITE}
            >
              This action cannot be undone. This will permanently delete your
              account and all associated data.
            </Text>

            <DeleteButton onClick={handleDeleteClick} />
          </div>
        </>

        {/* Error Alert */}
        {error && (
          <Alert
            message={error}
            type='error'
            showIcon
            css={css`
              margin-top: 16px;
            `}
          />
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={handleDeleteModalClose}
        onConfirm={handleDeleteConfirm}
        userEmail={userEmail}
        isLoading={isDeleting}
        error={error}
      />
    </Modal>
  )
}

export default UserSettingsModal
